import React, { createContext, useEffect, useMemo, useState } from 'react';

import useDeskpassAPI from '@/api/deskpass/useAPI';

import { useUserContext } from '@/context/User';

import useEvent from '@/hooks/useEvent';
import useStateMergeUpdater from '@/hooks/useStateMergeUpdater';
import useWatch from '@/hooks/useWatch';

import { consumerToHOC } from '@/lib/hoc';

const initialState = {
  reservation: null,
  validation: { isSelectedDateValid: true },
  currentMonthStart: null,
  dateSelected: false,

  // cached reservation (when user unauthenticated)
  reservationCache: null,
  isApplyingReservationCache: false,
  lastAppliedCacheToken: null,
};

const DeskBookingContext = createContext({});

export const useDeskBookingContext = () => {
  return React.useContext(DeskBookingContext);
};

export const DeskBookingProvider = ({ children }) => {
  const {
    authenticated,
    continueBookingAfterAuthSuccess,
    setContinueBookingAfterAuthSuccess,
  } = useUserContext();

  const [state, setState] = useState(initialState);

  const updateState = useStateMergeUpdater(setState);

  const resetReservationState = useEvent(() => {
    setState((prevState) => ({
      ...prevState,
      ...initialState,
    }));
  });

  const updateReservation = useEvent((data) => {
    updateState({ reservation: { ...state.reservation, ...data } });
  });
  const clearReservation = useEvent(() => {
    updateState({ reservation: null });
  });

  // Used to temporarily store reservation when user is not authenticated
  // and tries to book a desk. After successful auth, the reservation
  // is restored from this cache.
  const updateReservationCache = useEvent((reservation = null) => {
    if (!reservation) {
      updateState({ reservationCache: null });
      return;
    }
    updateState({ reservationCache: reservation });
  });

  const resetReservationCache = useEvent(() => {
    updateState({ reservationCache: null });
  });

  // Used by the DateModal handle route change when a user proceeds to the next step of the booking - usually to confirm reservation details modal
  const setDateSelected = useEvent((dateSelected = false) => {
    if (typeof dateSelected !== 'boolean') return;
    updateState({ dateSelected });
  });

  // when called by the DateModal, sets the current month being viewed
  // in the calendar, which is then used to load availability for that month
  const setCurrentMonthStart = useEvent((currentMonthStart) => {
    updateState({ currentMonthStart });
  });

  // Load space availability when reservation or month changes
  const [spaceAvailability, loadSpaceAvailability] = useDeskpassAPI(
    (api) => () => {
      if (!state?.reservation?.space || !state?.currentMonthStart) return null;
      return api.space.getAvailabilityOnDate(
        state.reservation.space,
        state.currentMonthStart,
      );
    },
    {
      onChange: [state.currentMonthStart],
      fireOnMount: false,
    },
  );

  // ===========
  // RESTORE RESERVATION AFTER AUTH
  // ===========
  // When a user was trying to book unauthenticated we cached a reservation.
  // After successful auth we apply that cached reservation into the context atomically and set flags so other flows skip overwriting.
  useWatch(
    () => {
      const { reservationCache } = state;
      if (
        !continueBookingAfterAuthSuccess ||
        !authenticated ||
        !reservationCache
      )
        return;

      // Apply cached reservation atomically and mark that we're applying it
      updateState((prev) => ({
        ...prev,
        reservation: { ...prev.reservation, ...reservationCache },
        isApplyingReservationCache: true,
      }));

      // Clear the global continue flag so this only happens once
      setContinueBookingAfterAuthSuccess(false);
      // DO NOT clear reservationCache here — wait until we detect the application succeeded
    },
    // deps: watch for changes that should trigger the restore attempt
    [continueBookingAfterAuthSuccess, authenticated, state.reservationCache],
  );

  // Watch for when the cached reservation appears to have been applied,
  // then clear the cache and reset the "applying" flag.
  useWatch(() => {
    if (!state.isApplyingReservationCache) return;

    const cache = state.reservationCache;
    if (!cache) {
      // Nothing to apply; flip flag off
      updateState({ isApplyingReservationCache: false });
      return;
    }

    // Compare using canonical fields: dateBooked + space.id
    const applied =
      state.reservation &&
      state.reservation.dateBooked === cache.dateBooked &&
      state.reservation.space?.id === cache.space?.id;

    if (applied) {
      // The cache has been applied; clear it and the applying flag
      resetReservationCache();
      updateState({ isApplyingReservationCache: false });
    }
    // Intentionally depend on the bits we check for "applied"
  }, [state.isApplyingReservationCache]);

  // Cleanup on provider unmount: reset reservation state
  useEffect(() => {
    return () => {
      resetReservationState();
    };
  }, []);

  // Compose memoized context value
  const context = useMemo(
    () => ({
      ...state,
      updateState,
      updateReservationCache,
      resetReservationCache,
      updateReservation,
      clearReservation,
      resetReservationState,
      setDateSelected,
      setCurrentMonthStart,
      spaceAvailability,
      loadSpaceAvailability,
    }),
    [
      state,
      updateState,
      updateReservationCache,
      resetReservationCache,
      updateReservation,
      clearReservation,
      resetReservationState,
      setDateSelected,
      setCurrentMonthStart,
      spaceAvailability,
      loadSpaceAvailability,
    ],
  );

  return (
    <DeskBookingContext.Provider value={context}>
      {children}
    </DeskBookingContext.Provider>
  );
};

export const withDeskBookingContext = consumerToHOC(
  DeskBookingContext.Consumer,
  'deskBookingContext',
);

export default DeskBookingContext;
